import { FC, useRef, useState } from "react";

import { ORG_PAGE_SOURCE, SOURCE_QUERY_PARAM_KEY } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";

import { RecaptchaAction } from "../../../../common/constants/recaptcha";
import { useRecaptchaRetry } from "../../../../common/hooks/useRecaptchaRetry";
import { NewsletterFormSignupData } from "./types";

import { getPreselectedCommunicationPreferences } from "./helpers/preselectedCommunicationPreferences";
import { buildAnswerDefaultValues, toSignupAnswersPayload } from "./helpers/questionAnswers";
import { NewsletterFormSignUpStateless } from "./NewsletterFormSignUp.stateless";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { NewsletterFormFrontendPageOutput } from "@/common/types/trpc";
import { PhoneNumberInputHandle } from "@/dashboard-design-system/PhoneNumberInput/types";

const extractCommunicationPreferenceIds = (
  preferences: NewsletterFormSignupData["communicationPreferencesSelected"]
): string[] => {
  if (!Array.isArray(preferences)) {
    return [];
  }

  return preferences.map((pref) => pref.value).filter((id) => id !== "");
};

export const NewsletterFormSignUp: FC<{
  newsletterForm: NewsletterFormFrontendPageOutput;
  isEmbedded?: boolean;
}> = ({ newsletterForm, isEmbedded }) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isAwaitingConfirmation, setIsAwaitingConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneInputKey, setPhoneInputKey] = useState(0);

  const { executeTrpc } = useRecaptchaRetry();

  const { mutateAsync: signupToNewsletterForm } = trpc.signupToNewsletterForm.useMutation();
  const searchParams = useSearchParams();
  const isFromOrganizationPage = searchParams?.get(SOURCE_QUERY_PARAM_KEY) === ORG_PAGE_SOURCE;

  const form = useForm<NewsletterFormSignupData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      language: "",
      address: "",
      zipCode: "",
      city: "",
      state: "",
      country: "",
      communicationPreferencesSelected: getPreselectedCommunicationPreferences(newsletterForm),
      communicationPreferencesInteracted: false,
      questions: buildAnswerDefaultValues(newsletterForm.questions),
    },
  });

  const startTime = useRef(Date.now());
  const phoneNumberInputRef = useRef<PhoneNumberInputHandle>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Selects and choice groups take no focusable ref, so react-hook-form cannot reach them and a blocked submit
  // looks like a dead button whenever the offending field sits above the fold.
  const scrollToFirstError = () => {
    requestAnimationFrame(() => {
      // The checkbox group styles its own error, so Mui-error never matches it
      const preferencesError = form.formState.errors.communicationPreferencesSelected
        ? formRef.current?.querySelector('[data-test="form-communication-preferences"]')
        : null;
      const firstError = formRef.current?.querySelector(".Mui-error") ?? preferencesError;
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const onSubmit = async (formData: NewsletterFormSignupData) => {
    setIsSubmitting(true);

    const processedPreferences = extractCommunicationPreferenceIds(formData.communicationPreferencesSelected);

    const { data, error } = await executeTrpc(
      RecaptchaAction.SignUpToNewsletterForm,
      formData.email,
      signupToNewsletterForm,
      {
        newsletterFormId: newsletterForm.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        language: formData.language,
        address: formData.address,
        zipCode: formData.zipCode,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        communicationPreferencesSelected: processedPreferences,
        communicationPreferencesInteracted: formData.communicationPreferencesInteracted,
        questions: toSignupAnswersPayload(formData.questions, newsletterForm.questions),
        source: isFromOrganizationPage ? ORG_PAGE_SOURCE : undefined,
      }
    );

    if (error) {
      if (error.code === "email_already_signed_up") {
        form.setError("email", {
          type: "manual",
          message: t("dashboard", "newsletterForms.signUp.alreadySignedUp"),
        });
      }
      if (error.code === "form_deleted" || error.code === "form_draft") {
        form.setError("email", {
          type: "manual",
          message: t("dashboard", "newsletterForms.signUp.notAcceptingSignUps"),
        });
      }
      // Without this the rejection is invisible and submit reads as a dead button
      if (error.code === "communication_preference_required") {
        form.setError("communicationPreferencesSelected", { type: "manual", message: t("common", "isRequired") });
      }
      scrollToFirstError();
      setIsSubmitting(false);
      return;
    }
    // Force phone input to remount by changing its key, which clears the library's internal state
    setPhoneInputKey((prev) => prev + 1);
    form.clearErrors();
    form.reset();

    setIsSubmitting(false);
    setIsAwaitingConfirmation(Boolean(data?.isAwaitingConfirmation));
    setIsSubmitted(true);

    logAmplitudeEvent(AmplitudeEvents.SignupFormSubmitted, {
      organizationId: newsletterForm.organization.id,
      formId: newsletterForm.id,
      formPath: newsletterForm.path,
      timeElapsed: Date.now() - startTime.current,
      isEmbedded,
      has_communication_preferences: Boolean(newsletterForm.communicationPreferences?.length),
    });
  };

  return (
    <form
      ref={formRef}
      onSubmit={form.handleSubmit(onSubmit, scrollToFirstError)}
      style={{ width: isEmbedded ? "100%" : "unset" }}
      onFocus={() => setIsSubmitted(false)}
    >
      <NewsletterFormSignUpStateless
        mainColor={newsletterForm.mainColor}
        buttonColor={newsletterForm.buttonColor}
        title={newsletterForm.title}
        submitState={isSubmitted ? "submitted" : isSubmitting ? "submitting" : "idle"}
        isAwaitingConfirmation={isAwaitingConfirmation}
        form={form}
        organization={newsletterForm.organization}
        isEmbedded={isEmbedded}
        hasFirstName={newsletterForm.hasFirstName}
        hasLastName={newsletterForm.hasLastName}
        hasPhoneNumber={newsletterForm.hasPhoneNumber}
        hasAddress={newsletterForm.hasAddress}
        hasLanguage={newsletterForm.hasLanguage}
        hasCommunicationPreferences={newsletterForm.hasCommunicationPreferences}
        isLegacyCommunicationPreferences={newsletterForm.isLegacyCommunicationPreferences}
        communicationPreferencesTitle={newsletterForm.communicationPreferencesTitle}
        communicationPreferences={newsletterForm.communicationPreferences}
        phoneNumberInputRef={phoneNumberInputRef}
        phoneInputKey={phoneInputKey}
        questions={newsletterForm.questions}
        locale={newsletterForm.locale}
      />
    </form>
  );
};
